import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { PageLayout } from "@/components/layout/PageLayout";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageInfoButton } from "@/components/page-info/PageInfoButton";
import { useBuildingDetail } from "@/hooks/buildings/useBuildingDetail";
import { useBuildingStore } from "@/hooks/useBuildingStore";
import type { BuildingRow } from "@/stores/buildings/models";
import { sanitizeString, sanitizeNumber } from "@/utils/sanitization";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import { formatAdsAddress } from "@/hooks/useAddressSearch";
import { formatBuildingAddress } from "@/utils/formatBuildingAddress";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

const EditBuilding = observer(function EditBuilding() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { detail, isLoading, error } = useBuildingDetail(id);
  const buildingStore = useBuildingStore();

  const formSchema = z.object({
    name: z.string().min(2, { message: t.buildings.nameMinLength }).transform(sanitizeString),
    address: z.string().min(5, { message: t.buildings.addressMinLength }).transform(sanitizeString),
    year_built: z
      .union([z.string(), z.number()])
      .transform((val) => {
        const numVal = typeof val === "string" ? parseInt(sanitizeNumber(val), 10) : val;
        return numVal;
      })
      .pipe(
        z
          .number()
          .min(1000, { message: t.forms.validation.invalidYear })
          .max(new Date().getFullYear(), { message: t.forms.validation.invalidYear })
      ),
    net_area: z
      .union([z.string(), z.number()])
      .optional()
      .transform((val) => {
        if (!val) return null;
        return typeof val === "string" ? parseFloat(sanitizeNumber(val)) : val;
      }),
  });

  const building = useMemo(() => {
    return detail?.base
      ? {
          ...detail.base,
          id: String(detail.base.id),
          created_by: detail.base.created_by ? String(detail.base.created_by) : undefined,
          org_id: String(detail.base.org_id),
        }
      : undefined;
  }, [detail?.base]);

  const form = useForm<z.input<typeof formSchema>, unknown, z.output<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      address: "",
      year_built: "" as string | number, // Will be transformed by Zod
      net_area: "" as string | number, // Will be transformed by Zod
    },
  });

  useEffect(() => {
    if (building) {
      form.reset({
        name: building.name || "",
        address: formatBuildingAddress(building.address),
        year_built: building.year_built || "",
        net_area: building.net_area ? building.net_area : "",
      });
    }
  }, [building, form]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      if (!id) throw new Error("Missing building id");
      // The form is seeded with the display-formatted address, not the stored
      // raw value — only write it back when the user actually edited the field.
      await buildingStore.updateBuildingFields(id, {
        name: values.name,
        ...(form.formState.dirtyFields.address ? { address: values.address } : {}),
        year_built: values.year_built as unknown as number,
        net_area: (values.net_area as unknown as number) ?? null,
      } as Partial<BuildingRow>);

      toast.success(t.buildings.updated);
      navigate(`/buildings/${id}`);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error updating building:", err);
      }
      toast.error(t.buildings.error);
    }
  }

  if (isLoading || !building) {
    return (
      <PageLayout>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <div className="flex h-64 items-center justify-center">
          <p className="text-destructive">{t.buildings.error}</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="p-6">
        <Card className="mx-auto w-full max-w-2xl rounded-3xl">
          <CardHeader>
            <div className="flex items-center gap-1.5">
              <CardTitle>{t.buildings.edit}</CardTitle>
              <PageInfoButton page="editBuilding" />
            </div>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.buildings.form.name}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t.buildings.form.name}
                          {...field}
                          className="rounded-xl"
                          onChange={(e) => field.onChange(sanitizeString(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.buildings.form.address}</FormLabel>
                      <FormControl>
                        <AddressAutocomplete
                          value={field.value}
                          onChange={field.onChange}
                          onSelect={(result) => {
                            field.onChange(formatAdsAddress(result));
                          }}
                          placeholder={t.buildings.form.address}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="year_built"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.buildings.form.yearBuilt}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t.common.yearPlaceholder}
                          {...field}
                          className="rounded-xl"
                          maxLength={4}
                          onChange={(e) => field.onChange(sanitizeNumber(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="net_area"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t.buildings.form.netArea}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder={t.buildings.form.netArea}
                          {...field}
                          className="rounded-xl"
                          onChange={(e) => field.onChange(sanitizeNumber(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full rounded-full"
                  {...pageInfoTarget("editBuilding.save")}
                >
                  {t.buildings.save}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
});

export default EditBuilding;
